/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Form from 'react-bootstrap/Form';
import { Button } from 'ui';
import Modal from 'react-bootstrap/Modal';
import { FormikProvider } from 'formik';
import { useLocation } from 'react-router-dom';
import styles from './UserDetails.module.css';
import { formatTimestamp } from 'lib/date';
import { COUNTRY_USA_VALIDATION_VARIANTS, eventNames } from 'utils/constants';
import { trackEvent, trackEventDuration } from 'lib/analytics';
import { ReactComponent as CloseIcon } from 'images/Close.svg';
import { ADDRESS_FIELD, USER_DETAIL_FORM, USER_DETAIL_FORM_CONDITIONS } from './helper/constants';
import { checkIfAddressFieldIsBlocked, getStates } from 'utils/utilities';
import { ROUTES } from 'utils/constants/routes';
import { getSsnFieldVisibility } from 'lib/ssnFallback';
import { scrollToTop } from 'lib/scrollToTop';
import { useUserDetailsForm, useAddressAutocomplete } from './hooks';
import { PersonalInfoSection, SsnSection, AddressSection } from './sections';

const UserDetails = (ownProps) => {
    const formData = useUserDetailsForm(ownProps);
    // Merge consumer props with the hook's Formik bag + Redux-derived values.
    // Hook values win on conflict — matches the former connect() mergeProps default.
    const props = { ...ownProps, ...formData };

    const {
        show,
        handleClose,
        confirmUserInformation,
        ssnConfig,
        isInformationMismatched,
        askForFallback,
        mandatoryFields,
        company,
    } = props;

    const { status, handleSubmit, isSubmitting, formik } = props;

    const submittedRef = useRef(false);
    const [isClosedAfterSubmit, setIsClosedAfterSubmit] = useState(false);

    const isUSA = COUNTRY_USA_VALIDATION_VARIANTS.includes(props?.values?.country?.toLowerCase());

    const {
        localValue,
        addressList,
        isAddressLoading,
        handleDropdownValueChange,
        handleDropdownValueSelect,
    } = useAddressAutocomplete(formik);

    const location = useLocation();
    const isRiskAssessmentRoute = location?.pathname === ROUTES.RISK_ASSESSMENT;

    const { ssnBlocked, addressBlocked, zipCodeBlocked } = useMemo(() => {
        return checkIfAddressFieldIsBlocked({
            companyUUID: company?.uuid,
            mandatoryFields,
            isRiskAssessmentRoute,
            userDetails: props?.userDetails,
        });
    }, [isRiskAssessmentRoute, props?.values?.zipCode, company?.uuid, mandatoryFields]);

    useEffect(() => {
        const isFormCompleted = status?.status === 'COMPLETED';
        if (isFormCompleted) {
            trackEvent({
                event: eventNames.confirmUserInfo,
                uac: props.user.uuid,
                otherValues: {
                    'Completion Time': formatTimestamp(new Date()),
                },
            });
            submittedRef.current = true;
            props.onSuccess();
        }
    }, [status]);

    // Scroll to the top after a submit btn clicked in form closes. The next screen shows on the same route, so
    // nothing else resets the scroll. This runs after the modal unlocks the page, because Safari
    // ignores scrollTo while the page is locked.
    useEffect(() => {
        if (!isClosedAfterSubmit) return;

        setIsClosedAfterSubmit(false);
        scrollToTop();
    }, [isClosedAfterSubmit]);

    useEffect(() => {
        if (props.user) {
            trackEventDuration({
                event: eventNames.confirmUserInfo,
                uac: props.user.uuid,
            });
        }
    }, []);

    const conditionallyRequired = (field) => {
        if (isRiskAssessmentRoute) {
            // if the zip code is not provided, then the zip code is required.
            if (field === 'zipCode' && !zipCodeBlocked) {
                return '*';
            }

            return addressBlocked ? '' : '*';
        }

        if (
            (ADDRESS_FIELD.includes(field) && mandatoryFields.includes('address')) ||
            (field === 'phone' && mandatoryFields.includes('phone_number'))
        ) {
            return '*';
        }

        return '';
    };

    const isMiddleNameRequired = props.values.acceptMiddleName ? null : '*';
    const emailVerified = props?.user?.email_verified;
    const phoneVerified = props?.user?.phone_verified;

    const handleCheckbox = () => {
        if (!props.values.acceptMiddleName) {
            props.setFieldValue('middleName', '');
        }
        props.setFieldValue('acceptMiddleName', !props.values.acceptMiddleName);
    };

    const ssnType = askForFallback ?? ssnConfig?.type;
    const hideCloseButton = !askForFallback && !isInformationMismatched;
    const disableField = isInformationMismatched || askForFallback;

    const shouldShowSsnFiled = getSsnFieldVisibility({ ssnConfig, askForFallback, ssnBlocked });

    const userDetailForm = {
        heading: () => {
            if (isInformationMismatched) {
                return USER_DETAIL_FORM.HEADING[USER_DETAIL_FORM_CONDITIONS.INFORMATION_MISMATCH];
            } else if (askForFallback) {
                return USER_DETAIL_FORM.HEADING[USER_DETAIL_FORM_CONDITIONS.REQUIRE_FALLBACK];
            } else {
                return USER_DETAIL_FORM.HEADING[USER_DETAIL_FORM_CONDITIONS.DEFAULT];
            }
        },
        description: () => {
            if (isInformationMismatched) {
                return USER_DETAIL_FORM.DESCRIPTION[
                    USER_DETAIL_FORM_CONDITIONS.INFORMATION_MISMATCH
                ];
            } else if (askForFallback) {
                return USER_DETAIL_FORM.DESCRIPTION[USER_DETAIL_FORM_CONDITIONS.REQUIRE_FALLBACK];
            } else {
                return USER_DETAIL_FORM.DESCRIPTION[USER_DETAIL_FORM_CONDITIONS.DEFAULT];
            }
        },
    };

    const isSsnRequired = !props?.values?.country || (ssnConfig?.type && isUSA) || askForFallback;

    const getStatesOptions = () => {
        const country = props?.values?.country;
        let states = getStates(country);

        // remove states for Slovenia and Slovakia as they are not correct data from the API
        if (country === 'SVN' || country === 'SVK') {
            states = [];
        }

        return states;
    };

    const handleFormClose = () => {
        formik.resetForm();
        handleClose();
    };

    // The modal unlocks the page only after this runs, so the scroll waits for the next render.
    const handleExited = () => {
        if (!submittedRef.current) return;

        submittedRef.current = false;
        setIsClosedAfterSubmit(true);
    };

    return (
        <FormikProvider value={formik}>
            <Modal
                show={show}
                onHide={handleFormClose}
                onExited={handleExited}
                className={styles.userDetailsModal}
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header className={styles.userDetailHeader}>
                    {hideCloseButton && (
                        <CloseIcon className={styles.closeBtn} onClick={handleFormClose} />
                    )}
                    <Modal.Title className={styles.headerTxt}>
                        {userDetailForm.heading()}
                    </Modal.Title>
                </Modal.Header>

                <Form onSubmit={handleSubmit} className={styles.userDetailForm}>
                    <Modal.Body className={styles.cardBody}>
                        <div className={styles.descriptionBox}>
                            <p className={styles.description}>{userDetailForm.description()}</p>
                        </div>

                        <PersonalInfoSection
                            confirmUserInformation={confirmUserInformation}
                            source={props.source}
                            disableField={disableField}
                            isMiddleNameRequired={isMiddleNameRequired}
                            emailVerified={emailVerified}
                            phoneVerified={phoneVerified}
                            conditionallyRequired={conditionallyRequired}
                            handleCheckbox={handleCheckbox}
                        />

                        <SsnSection
                            show={shouldShowSsnFiled}
                            isSsnRequired={isSsnRequired}
                            ssnType={ssnType}
                            isInformationMismatched={isInformationMismatched}
                            askForFallback={askForFallback}
                        />

                        <AddressSection
                            confirmUserInformation={confirmUserInformation}
                            source={props.source}
                            disableField={disableField}
                            addressBlocked={addressBlocked}
                            zipCodeBlocked={zipCodeBlocked}
                            isUSA={isUSA}
                            conditionallyRequired={conditionallyRequired}
                            localValue={localValue}
                            addressList={addressList}
                            isAddressLoading={isAddressLoading}
                            handleDropdownValueChange={handleDropdownValueChange}
                            handleDropdownValueSelect={handleDropdownValueSelect}
                            getStatesOptions={getStatesOptions}
                        />
                    </Modal.Body>
                    <Modal.Footer className={styles.userDetailFooter}>
                        <Button
                            variant="outline-primary"
                            className={styles.footerBtn}
                            onClick={handleFormClose}
                            disabled={disableField}
                        >
                            Cancel
                        </Button>

                        <Button
                            variant="primary"
                            disabled={isSubmitting}
                            className={styles.footerBtn}
                            type="submit"
                            loading={isSubmitting}
                        >
                            Confirm
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </FormikProvider>
    );
};

UserDetails.propTypes = {
    show: PropTypes.bool,
    handleClose: PropTypes.func,
    confirmUserInformation: PropTypes.bool,
    values: PropTypes.object,
    user: PropTypes.object,
    onSuccess: PropTypes.func,
    status: PropTypes.object,
    handleSubmit: PropTypes.func,
    isSubmitting: PropTypes.bool,
    source: PropTypes.string,
    setFieldValue: PropTypes.func,
    ssnConfig: PropTypes.object,
    isInformationMismatched: PropTypes.bool,
    askForFallback: PropTypes.any,
    mandatoryFields: PropTypes.array,
    company: PropTypes.object,
    userDetails: PropTypes.object,
    formik: PropTypes.object,
};

export default UserDetails;
